import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { hotelPartnerCurationKeys } from "@/components/pages/guesteria-partners/hooks/hotel-partner-curation.hooks";
import type { PartnerDestination } from "@/types/partner-settings";
import { partnerSettingsKeys } from "../hooks/partner-settings.hooks";

/**
 * After a city change the activity list is replaced by a background sync (about
 * 58 s on dev). The selector waits this long before it takes another change,
 * then the service and curation lists are read again.
 */
export const SYNC_WAIT_MS = 90_000;

/** Pause after the last keystroke before the search runs. */
export const SEARCH_DEBOUNCE_MS = 250;

export const normalizeDestinationQuery = (value: string): string =>
  value.trim().replace(/\s+/g, " ");

export const destinationLabel = (
  destination: Pick<PartnerDestination, "name" | "countryName">,
): string =>
  destination.countryName
    ? `${destination.name}, ${destination.countryName}`
    : destination.name;

export type DestinationSelectState =
  | "idle"
  | "loading"
  | "error"
  | "results"
  | "empty-can-search"
  | "remote-error"
  | "not-found";

interface StateInput {
  query: string;
  results: PartnerDestination[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** "City search" (provider) was started for this query. */
  remoteTried: boolean;
  remoteLoading: boolean;
  remoteError: boolean;
}

/**
 * What the dropdown shows. The provider button ("City search") appears only
 * when the cache has no match for the query.
 */
export const destinationSelectState = (
  input: StateInput,
): DestinationSelectState => {
  if (!normalizeDestinationQuery(input.query)) return "idle";
  if (input.isLoading || input.remoteLoading) return "loading";
  if (input.remoteTried && input.remoteError) return "remote-error";
  if (input.isError) return "error";
  if ((input.results?.length ?? 0) > 0) return "results";
  return input.remoteTried ? "not-found" : "empty-can-search";
};

/*
 * Wait for the activity sync after a city change, kept per hotel at module
 * level. The wait and the re-read of the lists must outlive the component that
 * started them (the user leaves the page, the city selector is closed, another
 * hotel is picked), so nothing here is tied to a React component. One timer per
 * hotel; the hotel id is the one the change was made under.
 */
interface SyncWait {
  deadline: number;
  timer: ReturnType<typeof setTimeout>;
}

const syncWaits = new Map<string, SyncWait>();
const syncWaitListeners = new Set<() => void>();

const notifySyncWait = (): void => {
  syncWaitListeners.forEach((listener) => listener());
};

export const subscribeSyncWait = (listener: () => void): (() => void) => {
  syncWaitListeners.add(listener);
  return () => {
    syncWaitListeners.delete(listener);
  };
};

/** Time (ms since epoch) the wait for this hotel ends, or null when none runs. */
export const getSyncWaitDeadline = (
  hotelId: string | null | undefined,
): number | null =>
  hotelId ? (syncWaits.get(hotelId)?.deadline ?? null) : null;

export const isSyncWaiting = (
  hotelId: string | null | undefined,
  now: number = Date.now(),
): boolean => {
  const deadline = getSyncWaitDeadline(hotelId);
  return deadline !== null && deadline > now;
};

/**
 * Starts the wait for the hotel: until `SYNC_WAIT_MS` pass, `isSyncWaiting` is
 * true; then the service list and the curation of that hotel are read again.
 * A second call for the same hotel replaces the timer (a later change starts a
 * new sync that must finish too), so there is never more than one per hotel.
 */
export const startSyncWait = (
  hotelId: string,
  queryClient: Pick<QueryClient, "invalidateQueries">,
  waitMs: number = SYNC_WAIT_MS,
): number => {
  const previous = syncWaits.get(hotelId);
  if (previous) clearTimeout(previous.timer);
  const deadline = Date.now() + waitMs;
  const timer = setTimeout(() => {
    syncWaits.delete(hotelId);
    notifySyncWait();
    queryClient
      .invalidateQueries({ queryKey: partnerSettingsKeys.services(hotelId) })
      .catch(() => undefined);
    queryClient
      .invalidateQueries({
        queryKey: hotelPartnerCurationKeys.curation(hotelId),
      })
      .catch(() => undefined);
  }, waitMs);
  syncWaits.set(hotelId, { deadline, timer });
  notifySyncWait();
  return deadline;
};

/** True while the activity sync of this hotel's last city change is awaited. */
export const useSyncWaiting = (hotelId: string | null | undefined): boolean =>
  useSyncExternalStore(
    subscribeSyncWait,
    () => isSyncWaiting(hotelId),
    () => false,
  );
